function restoreFocus(element: Element | null | undefined): void {
  if (
    !element?.isConnected ||
    typeof (element as HTMLElement).focus !== "function"
  ) {
    return;
  }
  try {
    (element as HTMLElement).focus({ preventScroll: true });
  } catch {
    try {
      (element as HTMLElement).focus();
    } catch {
      // A detached or disabled element may reject focus; copying still completed.
    }
  }
}

/**
 * Copy text to the clipboard, returning `true` on success and `false` on failure.
 *
 * Prefers the async Clipboard API and falls back to a hidden textarea +
 * `execCommand("copy")` for older browsers and insecure (non-HTTPS) contexts
 * where `navigator.clipboard` is unavailable or rejects (e.g. permission
 * denied). Callers should surface failure to the user instead of assuming the
 * copy succeeded.
 */
export async function writeToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Clipboard API can reject in insecure contexts or when permission is
    // denied — fall through to the legacy approach below.
  }

  if (typeof document === "undefined") {
    return false;
  }

  const previousFocus = document.activeElement;
  let textarea: HTMLTextAreaElement | null = null;
  try {
    textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    try {
      textarea?.remove();
    } catch {
      // Cleanup must not turn a copy result into an exception.
    }
    if (previousFocus !== document.body) {
      restoreFocus(previousFocus);
    }
  }
}

/**
 * Copy text while preserving the Clipboard API's reject-on-failure contract.
 *
 * Use this adapter when existing code already handles a rejected clipboard
 * promise in `try`/`catch`. New code should normally prefer
 * `writeToClipboard()` and branch on its boolean result.
 */
export async function writeToClipboardOrThrow(text: string): Promise<void> {
  if (!(await writeToClipboard(text))) {
    throw new Error("Unable to copy text to the clipboard");
  }
}

/**
 * Put formatted text on the clipboard, with a plain-text twin.
 *
 * A citation copied as plain text loses its italics, and re-italicising a journal title
 * by hand is exactly the chore the generator exists to remove. `ClipboardItem` carries
 * both flavours, so Word and Google Docs paste the HTML and a plain-text field gets
 * `plain`. Where the async clipboard or `ClipboardItem` is missing — older browsers,
 * insecure contexts, a denied permission — it falls back to plain text, which is still
 * a correct citation minus the italics.
 */
export async function copyRichText(
  html: string,
  plain: string
): Promise<boolean> {
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard?.write &&
      typeof ClipboardItem !== "undefined"
    ) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      return true;
    }
  } catch {
    // Permission denied or an unsupported flavour: plain text below still works.
  }
  return writeToClipboard(plain);
}
